import { gql } from "@apollo/client"
import { useQuery } from "@oysterhr/core"
import { useAccountAspect } from "@oysterhr/identity"
import { FeatureFlag as FeatureFlagEnum } from "@oysterhr/schema"
import { createContext, PropsWithChildren, useMemo } from "react"
import { FeatureFlags, FeatureFlagsResponse } from "./types"
import { convertFeatureFlagEnumToFeatureFlagKey } from "./utils"

export const FeatureFlagsContext = createContext<FeatureFlags | null>(null)
type FeatureFlagsProviderProps = PropsWithChildren<unknown>

const FEATURE_FLAGS_QUERY = gql`
  query FeatureFlags($actorId: ID) {
    featureFlags(actorId: $actorId)
  }
`

function useAspectActorId(): string | null {
  const { currentAspect } = useAccountAspect()
  const id = currentAspect.id

  return /^(Company|Engagement):/.test(id) ? id : null
}

/**
 * This provider queries the values of our feature flags, allowing them to be used synchronously
 * throughout the frontend codebase.
 */
export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const actorId = useAspectActorId()

  const { data, error } = useQuery<FeatureFlagsResponse>(FEATURE_FLAGS_QUERY, {
    variables: {
      actorId,
    },
  })

  if (error) {
    throw error
  }

  const featureFlags = useMemo(() => {
    if (!data) {
      return null
    }

    return Object.values(FeatureFlagEnum).reduce((flags, featureFlag) => {
      const key = convertFeatureFlagEnumToFeatureFlagKey(featureFlag)
      flags[key] = data.featureFlags.includes(featureFlag)
      return flags
    }, {} as FeatureFlags)
  }, [data])

  // Don't render the child content until the feature flags have been loaded. This ensures that
  // feature flags are always available synchronously.
  if (!featureFlags) {
    return null
  }

  // TODO: This provider should only render its children once the feature flags have been loaded.
  return (
    <FeatureFlagsContext.Provider value={featureFlags ?? null}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
